
import _ from 'lodash'
import { FORMER_ICHRA_MESSAGE } from '~/util/former-ichra'
import { judgeFoundGroup } from '~/util/group-find'

const NON_PARTICIPATING_ERROR = 'We\'re sorry - it looks like this group does not participate in our program. Please check your spelling or reach out to our team at <a href="mailto:help@doctorschoiceusa.com">help@doctorschoiceusa.com</a> if you think this may be an error.'

export default {
    props: {
        initialQuery: {
            type: String,
            default: ''
        },
        value: {
            type: [String, Number],
            default: null,
        },
        minQuery: {
            type: Number,
            default: 2,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: 'Space Force',
        },
        btnLabel: {
            type: String,
            default: 'Find & Verify Group'
        },
        helpText: {
            type: String,
            default: null,
        },
        nonParticipatingAlert: {
            type: Boolean,
            default: true
        },
        levels: {
            type: Array,
            default: () => ['paying', 'financial_advisor', 'dpc']
        }
    },
    data () {
        return {
            query: '',
            error: null,
            groupValue: null,
        }
    },
    async fetch () {
        this.error = null
        this.groupValue = null
        if (this.minQuery && this.query.length < this.minQuery) {
            return
        }
        this.loading = true
        const data = await this.$try('findGroup', { query: this.query }, { alert: false, spinner: false })
        this.loading = false
        if (data.error) {
            this.error = data.error
            this.groupValue = null
        } else {
            const verdict = data ? this.judge(data) : 'ok'
            if (verdict === 'former_ichra') {
                this.declineFormerIchra(data)
            } else if (verdict === 'non_participating') {
                this.handOffNonParticipating(data)
            } else if (verdict === 'error') {
                this.error = NON_PARTICIPATING_ERROR
                this.groupValue = null
            } else {
                this.error = null
                this.groupValue = data || null
            }
        }
    },
    computed: {},
    watch: {
        value () {
            if (!_.isEqual(parseInt(this.value), this.groupValue?.id)) {
                this.updateValue()
            }
        },
        groupValue () {
            this.emitInput()
        },
        query (q) {
            return this.onQuery(q)
        }
    },
    mounted () {
        this.query = this.initialQuery || ''
        // When handed a concrete group id (V2 slug prefill or a resumed
        // selection), resolve + display it by id and skip the initial name
        // search: findGroup matches on an exact lowercased name and would
        // spuriously clear an already-known group. updateValue() alone shows
        // it as verified. With no id, fall back to the normal search.
        if (this.value) {
            this.updateValue()
        } else {
            this.$fetch()
        }
    },
    methods: {
        // A former Benefit Bay employer (closed 2026-10-01, spec D9): never
        // verified, but the visitor is told why. A parent that listens (the V2
        // eligibility step) shows the message itself; everyone else gets it
        // here, in the error slot. find/group/ says it as `former_ichra`,
        // getGroup(id) as `config.former_ichra`.
        declineFormerIchra (group) {
            this.groupValue = null
            if (this.$listeners['former-ichra']) {
                this.error = null
                this.$emit('former-ichra', group)
            } else {
                this.error = FORMER_ICHRA_MESSAGE
            }
        },
        // One verdict for both ways a group arrives (#730 final review C2).
        judge (group) {
            return judgeFoundGroup(group, this.levels, {
                nonParticipatingAlert: this.nonParticipatingAlert,
                canHandOff: !!this.$listeners['non-participating'],
            })
        },
        // #730: a group that participates at no level, with a parent that wants
        // to decide (the V2 wizard asks the client's state and books FL/RI
        // residents directly). Never "picked": the parent keeps the id itself.
        handOffNonParticipating (group) {
            this.groupValue = null
            this.error = null
            this.$emit('non-participating', group)
        },
        async updateValue () {
            if (this.value) {
                const group = await this.$api.getGroup(this.value)
                // By id (a slug landing, a resumed session, the parent handing the
                // id back): the same two carve-outs as a name search. A group that
                // merely fails the asked levels still verifies by id, as before.
                const verdict = group ? this.judge(group) : 'ok'
                if (verdict === 'former_ichra') {
                    this.declineFormerIchra(group)
                    return
                }
                if (verdict === 'non_participating') {
                    this.handOffNonParticipating(group)
                    return
                }
                this.groupValue = group
            } else {
                this.groupValue = null
            }
        },
        emitInput () {
            this.$emit('input', this.groupValue?.id || null)
            this.$emit('input-raw', this.groupValue || null)
        },
        onQuery (query) {
            if (!query) {
                this.groupValue = null
                this.error = null
            }
            this.query = query
            this.$emit('input-query', this.query)
        },
    },
}
